import { useState, useEffect } from 'react';
import adminService from '@/services/adminService';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Users,
  TrendingUp,
  FileCheck,
  DollarSign,
  Building2,
  UserPlus,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
} from 'lucide-react';

/**
 * İstatistik kartı bileşeni
 */
function StatCard({ title, value, icon: Icon, color, subtitle }) {
  return (
    <Card>
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-muted-foreground">{title}</p>
            <p className="text-2xl font-bold mt-1">{value}</p>
            {subtitle && (
              <p className="text-xs text-muted-foreground mt-1">{subtitle}</p>
            )}
          </div>
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${color}`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Admin Dashboard Sayfası
 * Genel istatistikler ve özet bilgiler.
 */
export default function AdminDashboardPage() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setLoading(true);
    const { data, error: err } = await adminService.getDashboardStats();
    if (err) {
      setError(err.message || 'İstatistikler yüklenirken hata oluştu');
    } else {
      setStats(data);
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 8 }).map((_, i) => (
            <Card key={i}>
              <CardContent className="pt-6">
                <Skeleton className="h-4 w-24 mb-2" />
                <Skeleton className="h-8 w-16" />
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-red-200 bg-red-50">
        <CardContent className="pt-6">
          <div className="flex items-center gap-3 text-red-600">
            <AlertCircle className="w-5 h-5" />
            <p>{error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  return (
    <div className="space-y-6">
      {/* Kullanıcı İstatistikleri */}
      <div>
        <h2 className="text-lg font-semibold text-[#0A192F] mb-3 flex items-center gap-2">
          <Users className="w-5 h-5 text-blue-600" />
          Kullanıcılar
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Toplam Kullanıcı"
            value={stats?.total_users || 0}
            icon={Users}
            color="bg-blue-600"
          />
          <StatCard
            title="Yatırımcılar"
            value={stats?.total_investors || 0}
            icon={TrendingUp}
            color="bg-green-600"
          />
          <StatCard
            title="Personel"
            value={stats?.total_staff || 0}
            icon={Users}
            color="bg-purple-600"
          />
          <StatCard
            title="Bu Ay Yeni"
            value={stats?.new_users_this_month || 0}
            icon={UserPlus}
            color="bg-cyan-600"
          />
        </div>
      </div>

      {/* Gayrimenkul İstatistikleri */}
      <div>
        <h2 className="text-lg font-semibold text-[#0A192F] mb-3 flex items-center gap-2">
          <Building2 className="w-5 h-5 text-amber-600" />
          Gayrimenkuller
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Toplam İlan"
            value={stats?.total_properties || 0}
            icon={Building2}
            color="bg-amber-600"
          />
          <StatCard
            title="Aktif İlan"
            value={stats?.active_properties || 0}
            icon={CheckCircle}
            color="bg-green-600"
          />
        </div>
      </div>

      {/* Yatırım İstatistikleri */}
      <div>
        <h2 className="text-lg font-semibold text-[#0A192F] mb-3 flex items-center gap-2">
          <TrendingUp className="w-5 h-5 text-green-600" />
          Yatırımlar
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Toplam Yatırım"
            value={stats?.total_investments || 0}
            icon={TrendingUp}
            color="bg-green-600"
          />
          <StatCard
            title="Aktif Yatırım"
            value={stats?.active_investments || 0}
            icon={Clock}
            color="bg-blue-600"
          />
          <StatCard
            title="Tamamlanan"
            value={stats?.completed_investments || 0}
            icon={CheckCircle}
            color="bg-emerald-600"
          />
          <StatCard
            title="Bu Ay Yeni"
            value={stats?.new_investments_this_month || 0}
            icon={UserPlus}
            color="bg-cyan-600"
          />
        </div>
      </div>

      {/* Belge İstatistikleri */}
      <div>
        <h2 className="text-lg font-semibold text-[#0A192F] mb-3 flex items-center gap-2">
          <FileCheck className="w-5 h-5 text-indigo-600" />
          Belgeler
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Toplam Belge"
            value={stats?.total_documents || 0}
            icon={FileCheck}
            color="bg-indigo-600"
          />
          <StatCard
            title="Bekleyen"
            value={stats?.pending_documents || 0}
            icon={Clock}
            color="bg-yellow-600"
            subtitle="Onay bekliyor"
          />
          <StatCard
            title="Onaylanan"
            value={stats?.verified_documents || 0}
            icon={CheckCircle}
            color="bg-green-600"
          />
          <StatCard
            title="Reddedilen"
            value={stats?.rejected_documents || 0}
            icon={XCircle}
            color="bg-red-600"
          />
        </div>
      </div>

      {/* Finansal İstatistikler */}
      <div>
        <h2 className="text-lg font-semibold text-[#0A192F] mb-3 flex items-center gap-2">
          <DollarSign className="w-5 h-5 text-emerald-600" />
          Finansal
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard
            title="Toplam İşlem"
            value={stats?.total_transactions || 0}
            icon={DollarSign}
            color="bg-emerald-600"
          />
          <StatCard
            title="Toplam Tutar"
            value={formatCurrency(stats?.total_transaction_amount)}
            icon={DollarSign}
            color="bg-[#D4AF37]"
            subtitle="Tamamlanan işlemler"
          />
        </div>
      </div>
    </div>
  );
}
